import { creditCardPaymentDisplayName, uniqueDisplayAccount } from "../utils/localizedAccountDisplayName";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/PurchaseEntry.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TrashIcon,
  ArrowLeftIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/Button";
import { apiGet, apiPost } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import { saveDraft, loadDraft, clearDraft } from "../utils/localStorageDraft";
import {
  readLastPaymentMethod,
  writeLastPaymentMethod,
} from "../utils/lastPaymentMethod";
import { companyRoute } from "../utils/routeHelpers";
import { formatMoney } from "../utils/formatNumber";
import {
  canonicalLineTotal,
  deriveCostFromTotal,
  exceeds2dp,
} from "../utils/purchaseLineCalc";
import VendorEntry from "./VendorEntry";
import ProductEntry from "./ProductEntry";
import { ymdTodayLocal } from "../utils/dateUtils";
import { A } from "../styles/appleClasses";
import MemoSection from "../components/MemoSection";
import {
  extractApiDetailOutcome,
  formatClosedThroughDate,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";
import {
  scannerHandoffFromRouteState,
  type ScannerHandoff,
} from "../types/scannerHandoff";
import {
  canonicalPurchaseCreate,
  classifyPurchaseCreateFailure,
  decidePurchaseSubmissionIdentity,
  fingerprintPurchaseCreate,
  getOrCreatePurchaseIdempotencyKey,
  randomUuidKey,
} from "../utils/purchaseCreateIdempotency";

const PURCHASE_DRAFT_KEY = "ricebook_purchaseentry_draft";

type Line = {
  product_id: string;
  qty: string;
  cost_price: string;
  discount: string;
  total: string;
  lastEdited?: "cost_price" | "discount" | "total";
  // PURCHASE-LINE-ROUND-STAGE-1 editable-total UX:
  //  * totalError blocks submission (invalid/>2dp total, or nonzero total at
  //    100% discount) and is shown as a field error.
  //  * totalNotice is informational (e.g. "adjusted to 29.01" when the entered
  //    total was not exactly representable) and does NOT block submission.
  totalError?: string;
  totalNotice?: string;
};

type Product = {
  id: number;
  name: string;
  sell_price?: number;
  cost_price?: number;
};

type Vendor = {
  id: number;
  name: string;
  email?: string;
};

type Account = {
  id: number;
  name: string;
  type?: string;
  kind: string;
  is_system?: boolean;
};

type CreatedPurchase = {
  id: number;
  bill_id?: number | null;
};

const defaultLine: Line = {
  product_id: "",
  qty: "1",
  cost_price: "0",
  discount: "0",
  total: "0",
  lastEdited: undefined,
};

function getLineTotal(qty: string, cost_price: string, discount: string) {
  // PURCHASE-LINE-ROUND-STAGE-1: canonical net-unit-first total via exact
  // BigInt arithmetic (matches the backend). ">2dp" / invalid inputs return
  // "0" for display; submit-time validation rejects them explicitly.
  const r = canonicalLineTotal(
    (qty ?? "").trim() === "" ? "1" : qty,
    (cost_price ?? "").trim() === "" ? "0" : cost_price,
    (discount ?? "").trim() === "" ? "0" : discount,
  );
  return r.ok ? r.totalText : "0";
}

function parseMoneyInput(v?: string | number | null) {
  const s = String(v ?? "")
    .replace(/,/g, "")
    .trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function toInputMoney(n: number) {
  const safe = Number.isFinite(n) ? n : 0;
  return String(Math.round(safe * 100) / 100);
}

// Display metadata is never persisted or passed to error classifiers.
type LocalPurchaseMessage = { key: ParseKeys; values?: Record<string, string | number>; closedDate?: string | null; field?: LocalPurchaseMessage; inner?: PurchaseMessage };
type PurchaseMessage = string | LocalPurchaseMessage;
const localMessage = (key: ParseKeys, values?: Record<string, string | number>): LocalPurchaseMessage => ({ key, values });
function localApiDetail(err: any): PurchaseMessage {
  const outcome = extractApiDetailOutcome(err);
  return outcome.kind === "raw" ? outcome.text : localMessage("sales.errors.generic");
}
function localClosedPeriod(closedDate?: string | null): LocalPurchaseMessage {
  return { key: "sales.errors.closedSubmit", closedDate };
}

// Provenance lives outside the line object: draft shape/raw strings stay intact.
const localLineMessages = new WeakSet<object>();
function markLocalLine<T extends object>(line: T): T {
  localLineMessages.add(line);
  return line;
}
function lineMessage(line: object, raw?: string): PurchaseMessage | undefined {
  if (!raw || !localLineMessages.has(line)) return raw;
  if (raw === "Total must be a valid amount with at most 2 decimal places.") return localMessage("purchases.errors.invalidTotal");
  if (raw === "At 100% discount the total must be 0.00.") return localMessage("purchases.errors.fullDiscount");
  const adjusted = /^Adjusted to (\d+\.\d{2})$/.exec(raw);
  return adjusted ? localMessage("purchases.errors.adjusted", { amount: adjusted[1] }) : raw;
}

export default function PurchaseEntry() {
  const { t } = useTranslation();
  const messageText = (message: PurchaseMessage | null | undefined): string => {
    if (message == null) return "";
    if (typeof message === "string") return message;
    const values = "closedDate" in message
      ? { date: message.closedDate ? formatClosedThroughDate(message.closedDate) : t("sales.errors.closedDate") }
      : { ...message.values, ...(message.field ? { field: messageText(message.field) } : {}), ...(message.inner !== undefined ? { error: messageText(message.inner) } : {}) };
    return t(message.key, values);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();
  const routeScannerHandoff = useMemo(
    () => scannerHandoffFromRouteState(location.state),
    [location.state],
  );
  const vendorMatchAttemptedRef = useRef(false);
  const skipLastPaymentPreferenceRef = useRef(false);
  const lastPaymentMethodAppliedRef = useRef(false);

  // POST /purchases idempotency key: one key per logical Purchase draft,
  // mirroring SaleEntry.tsx's saleIdempotencyKeyRef exactly (single-resource
  // "new document" form, not the per-resource Map pattern used by
  // PurchaseDetail.tsx's standalone Vendor Payment). Stable across
  // rerenders, retries, and ambiguous failures; cleared only on confirmed
  // success or a definite/closed-period/idempotency-conflict rejection --
  // see handleSubmit and purchaseCreateIdempotency.ts's module docstring.
  const purchaseIdempotencyKeyRef = useRef<string | null>(null);
  // Fingerprint of the exact business payload sent the FIRST time the
  // current idempotency key was used. A non-null value here doubles as
  // "there is a live (ambiguous or in-flight) attempt to compare a retry
  // against" -- it is cleared together with the key on every definite
  // outcome, so the two never drift apart.
  const submittedPurchaseFingerprintRef = useRef<string | null>(null);
  // Synchronous guard against duplicate handleSubmit entry -- React's
  // `saving` state alone only disables the button after a render commits,
  // which is not guaranteed before a second, near-simultaneous submit event
  // is handled.
  const purchaseSubmitInFlightRef = useRef(false);
  const draftAutosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftCompletedRef = useRef(false);

  const stopDraftAutosave = () => {
    draftCompletedRef.current = true;
    if (draftAutosaveTimerRef.current !== null) {
      clearTimeout(draftAutosaveTimerRef.current);
      draftAutosaveTimerRef.current = null;
    }
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);

  const [lines, setLines] = useState<Line[]>([{ ...defaultLine }]);
  const [vendorId, setVendorId] = useState("");
  const [vendorEmail, setVendorEmail] = useState("");
  const [date, setDate] = useState(ymdTodayLocal());
  // Due date is PREFILLED from the purchase date ("due on receipt") and follows
  // it until the user touches the field (dueDateDirty). The user may pick
  // another date or deliberately clear it; a cleared field is sent as null.
  const [dueDate, setDueDate] = useState(ymdTodayLocal());
  const [dueDateDirty, setDueDateDirty] = useState(false);
  const onDueDateChange = (ymd: string) => {
    setDueDateDirty(true);
    setDueDate(ymd);
  };
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [memo, setMemo] = useState("");

  // Accounts + inline payment
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [paymentAccountId, setPaymentAccountId] = useState<number | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentError, setPaymentError] = useState<PurchaseMessage | null>(null);
  const [onAccount, setOnAccount] = useState<boolean>(false);
  const [paymentTouched, setPaymentTouched] = useState<boolean>(false);
  const [error, setError] = useState<PurchaseMessage>("");
  const [saving, setSaving] = useState(false);
  const [appliedScannerHandoff, setAppliedScannerHandoff] =
    useState<ScannerHandoff | null>(null);
  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const [showVendorInlay, setShowVendorInlay] = useState(false);
  const [productInlayIndex, setProductInlayIndex] = useState<number | null>(
    null,
  );

  function resetPurchaseEntry() {
    const today = ymdTodayLocal();

    setLines([{ ...defaultLine }]);
    setVendorId("");
    setVendorEmail("");
    setDate(today);
    setDueDate(today);
    setInvoiceNumber("");
    setMemo("");
    setError("");
    setPaymentAccountId(null);
    setPaymentAmount("");
    setPaymentError(null);
    setOnAccount(false);
    setPaymentTouched(false);
    setAppliedScannerHandoff(null);

    // A fresh blank draft is a genuinely new logical Purchase -- called
    // both after confirmed success and on explicit discard. Every caller of
    // resetPurchaseEntry() also calls clearDraft(PURCHASE_DRAFT_KEY), which
    // removes the persisted copy (see the save-draft effect below). Always
    // clear both together -- never one without the other -- mirroring
    // SaleEntry.tsx's resetSaleEntry.
    purchaseIdempotencyKeyRef.current = null;
    submittedPurchaseFingerprintRef.current = null;
  }

  // Atomic persistence: writes the key and the fingerprint of the payload
  // first sent under it into the existing localStorage draft in ONE
  // write, so reload restores the pair together. Called at first submit
  // and when a definite rejection clears the pair. Not folded into debounced
  // save-draft effect below: that effect's dependency array only reacts to
  // FORM FIELD state, and mutating a ref (as getOrCreatePurchaseIdempotencyKey
  // does) never schedules a rerender, so relying on it alone could leave a
  // freshly-minted key unpersisted until some unrelated field next changes.
  const persistPurchaseIdempotencyState = (key: string | null, fingerprint: string | null) => {
    const existingDraft = loadDraft(PURCHASE_DRAFT_KEY) || {};
    saveDraft(PURCHASE_DRAFT_KEY, {
      ...existingDraft,
      idempotencyKey: key,
      submittedPayloadFingerprint: fingerprint,
    });
  };

  const applyScannerHandoff = (handoff: ScannerHandoff) => {
    resetPurchaseEntry();

    setDate(handoff.dateYmd);
    setDueDate(handoff.dateYmd);
    vendorMatchAttemptedRef.current = false;
    setAppliedScannerHandoff(handoff);
  };

  // Load draft on mount
  useEffect(() => {
    if (routeScannerHandoff) {
      skipLastPaymentPreferenceRef.current = true;
      clearDraft(PURCHASE_DRAFT_KEY);
      applyScannerHandoff(routeScannerHandoff);
    } else {
      const draft = loadDraft(PURCHASE_DRAFT_KEY);
      if (draft) {
        skipLastPaymentPreferenceRef.current = true;
        setLines(
          (draft.lines || [{ ...defaultLine }]).map((line: Line) => ({
            ...line,
            total: line?.total
              ? toInputMoney(parseMoneyInput(line.total))
              : "0",
          })),
        );
        setVendorId(draft.vendorId || "");
        setVendorEmail(draft.vendorEmail || "");
        const today = ymdTodayLocal();
        setDate(draft.date || today);
        setDueDate(draft.dueDate || draft.date || today);
        setInvoiceNumber(draft.invoiceNumber || "");
        setMemo(draft.memo || "");

        if (draft.paymentAmount) {
          setPaymentAmount(toInputMoney(parseMoneyInput(draft.paymentAmount)));
          setPaymentTouched(true);
        }

        if (
          typeof draft.paymentAccountId === "number" ||
          typeof draft.paymentAccountId === "string"
        ) {
          const val = Number(draft.paymentAccountId);
          setPaymentAccountId(isNaN(val) ? null : val);
        }
        if (typeof draft.onAccount === "boolean") {
          setOnAccount(draft.onAccount);
        }
        setError("");

        // Restore the idempotency key and its matching fingerprint as an
        // associated pair -- mirrors SaleEntry.tsx's draft-load restore
        // logic exactly. A fingerprint is only ever trusted alongside a
        // restored key; a key restored WITHOUT a fingerprint (a legacy
        // draft saved before this existed) keeps the key and leaves the
        // fingerprint unbound, so the next genuine submit attempt binds
        // the current payload to it exactly like a true first attempt.
        if (typeof draft.idempotencyKey === "string" && draft.idempotencyKey) {
          purchaseIdempotencyKeyRef.current = draft.idempotencyKey;
          if (
            typeof draft.submittedPayloadFingerprint === "string" &&
            draft.submittedPayloadFingerprint
          ) {
            submittedPurchaseFingerprintRef.current =
              draft.submittedPayloadFingerprint;
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!appliedScannerHandoff || vendorMatchAttemptedRef.current) return;
    if (!vendors.length) return;

    vendorMatchAttemptedRef.current = true;
    if (vendorId) return;

    const normalized = appliedScannerHandoff.vendorText.trim().toLowerCase();
    const matches = vendors.filter(
      (vendor) => vendor.name.trim().toLowerCase() === normalized,
    );
    if (matches.length === 1) setVendorId(String(matches[0].id));
  }, [appliedScannerHandoff, vendorId, vendors]);

  // Read identity refs when the timer fires, including after a submit/rejection.
  useEffect(() => {
    if (draftCompletedRef.current) return;
    const handler = setTimeout(() => {
      draftAutosaveTimerRef.current = null;
      if (draftCompletedRef.current) return;
      saveDraft(PURCHASE_DRAFT_KEY, {
        lines,
        vendorId,
        vendorEmail,
        date,
        dueDate,
        invoiceNumber,
        memo,
        paymentAccountId,
        paymentAmount,
        onAccount,
        idempotencyKey: purchaseIdempotencyKeyRef.current,
        submittedPayloadFingerprint: submittedPurchaseFingerprintRef.current,
      });
    }, 300);
    draftAutosaveTimerRef.current = handler;
    return () => clearTimeout(handler);
  }, [
    lines,
    vendorId,
    vendorEmail,
    date,
    invoiceNumber,
    memo,
    paymentAccountId,
    paymentAmount,
    onAccount,
    dueDate,
  ]);

  useEffect(() => {
    // Non-account purchases do not send a due date; keep it aligned to the
    // purchase date and pristine so returning to on-account re-prefills.
    if (!onAccount) {
      setDueDate(date);
      setDueDateDirty(false);
      return;
    }

    // On-account: while untouched, the due date follows the purchase date (the
    // visible "due on receipt" default).
    if (!dueDateDirty) {
      setDueDate(date);
      return;
    }

    // Once touched: clamp an ENTERED date to be no earlier than the purchase
    // date. A cleared field ("") is a deliberate "no due date" and is left as-is.
    if (!dueDate) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return;
    if (dueDate < date) setDueDate(date);
  }, [date, onAccount, dueDate, dueDateDirty]);

  // Load reference data
  useEffect(() => {
    if (!company_id) return;

    apiGet<Product[]>(`/products?company_id=${company_id}`)
      .then(setProducts)
      .catch(() => setProducts([]));

    apiGet<Vendor[]>(`/vendors?company_id=${company_id}`)
      .then(setVendors)
      .catch(() => setVendors([]));
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((data) => setAccounts(data))
      .catch(() => setAccounts([]));
  }, [company_id]);

  // Payment accounts: cash/bank plus the system Credit Card Payable.
  const paymentAccounts = useMemo(() => {
    const priorityMap: Record<string, number> = {
      cash: 10,
      "bank transfer": 20,
      fps: 30,
      cheque: 40,
      "credit card": 50,
      alipay: 90,
      "wechat pay": 100,
      payme: 110,
      stripe: 120,
    };

    return accounts
      .filter(
        (a) =>
          (((a.type || "").toLowerCase() === "asset" &&
            (a.kind?.toLowerCase() === "bank" ||
              a.kind?.toLowerCase() === "cash") &&
            !a.is_system) ||
            ((a.type || "").toLowerCase() === "liability" &&
              a.kind?.toLowerCase() === "credit_card_payable")),
      )
      .map((a) =>
        a.kind?.toLowerCase() === "credit_card_payable"
          ? { ...a, name: "Credit Card" }
          : a,
      )
      .sort((a, b) => {
        const aName = a.name.toLowerCase().trim();
        const bName = b.name.toLowerCase().trim();

        const aPriority = priorityMap[aName] ?? 999;
        const bPriority = priorityMap[bName] ?? 999;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        return a.name.localeCompare(b.name);
      });
  }, [accounts]);

  // Add "On account" as a synthetic option to the payment account list
  const paymentAccountOptions = useMemo<Account[]>(
    () => [{ id: -1, name: "On account", kind: "other" }, ...paymentAccounts],
    [paymentAccounts],
  );

  useEffect(() => {
    if (!company_id) return;
    if (lastPaymentMethodAppliedRef.current) return;
    if (skipLastPaymentPreferenceRef.current) return;
    if (onAccount || paymentAccountId !== null) return;

    const remembered = readLastPaymentMethod("purchases", company_id);
    if (!remembered) {
      lastPaymentMethodAppliedRef.current = true;
      return;
    }

    const baseDue = date || ymdTodayLocal();
    if (remembered === "on_account") {
      setOnAccount(true);
      setPaymentAccountId(null);
      setPaymentAmount("");
      setPaymentTouched(false);
      setDueDate(baseDue);
      lastPaymentMethodAppliedRef.current = true;
      return;
    }

    const rememberedAccountId = Number(remembered);
    if (
      Number.isFinite(rememberedAccountId) &&
      paymentAccounts.some((account) => account.id === rememberedAccountId)
    ) {
      setOnAccount(false);
      setPaymentAccountId(rememberedAccountId);
      setPaymentTouched(false);
      setDueDate(baseDue);
      lastPaymentMethodAppliedRef.current = true;
    }
  }, [company_id, date, onAccount, paymentAccountId, paymentAccounts]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  // Update vendor email when vendorId changes
  useEffect(() => {
    const found = vendors.find((v) => String(v.id) === vendorId);
    setVendorEmail(found?.email || "");
  }, [vendorId, vendors]);

  const isClosedDate = useMemo(() => {
    return isDateClosed(date, closedThroughDate);
  }, [date, closedThroughDate]);

  // Grand total
  const grandTotal = lines.reduce(
    (sum, line) => sum + parseMoneyInput(line.total),
    0,
  );
  const scannedTotal = appliedScannerHandoff
    ? parseMoneyInput(appliedScannerHandoff.amount)
    : null;
  const scannerDifferenceCents =
    scannedTotal === null
      ? null
      : Math.round((scannedTotal - grandTotal) * 100);

  // Default payment amount to full invoice amount (unless user has touched it or it's on account)
  useEffect(() => {
    if (onAccount) return;
    if (!paymentTouched) {
      if (grandTotal > 0) {
        setPaymentAmount(grandTotal.toFixed(2));
      } else {
        setPaymentAmount("");
      }
    }
  }, [grandTotal, onAccount, paymentTouched]);

  // Live guard for payment amount vs grand total
  useEffect(() => {
    if (onAccount || !paymentAmount) {
      setPaymentError(null);
      return;
    }
    const amt = parseMoneyInput(paymentAmount);
    if (amt < 0) {
      setPaymentError(localMessage("purchases.errors.negativePayment"));
    } else if (amt > grandTotal) {
      setPaymentError(
        localMessage("purchases.errors.paymentAboveTotal", { paid: amt.toFixed(2), total: grandTotal.toFixed(2) }),
      );
    } else {
      setPaymentError(null);
    }
  }, [paymentAmount, grandTotal, onAccount]);

  const handleItemChange = (idx: number, product_id: string) => {
    const item = products.find((i) => String(i.id) === product_id);
    setLines((l) =>
      l.map((line, i) =>
        i === idx
          ? {
              ...line,
              product_id,
              cost_price: item?.cost_price?.toString() ?? "0",
              discount: "0",
              qty: "1",
              total: getLineTotal(
                "1",
                item?.cost_price?.toString() ?? "0",
                "0",
              ),
              lastEdited: undefined,
              totalError: undefined,
              totalNotice: undefined,
            }
          : line,
      ),
    );
  };

  const handleLineChange = (
    idx: number,
    field: keyof Line,
    rawValue: string,
  ) => {
    setLines((lines) =>
      lines.map((line, i) => {
        if (i !== idx) return line;

        // PURCHASE-LINE-ROUND-STAGE-1: preserve the RAW typed text for qty /
        // cost_price / discount. Do NOT sanitizeInput or parseFloat here --
        // stripping "e"/","/"+" or coercing would silently turn malformed
        // input (e.g. "1e2") into a plausible value ("12") before the strict
        // grammar/precision validator can inspect it. Malformed / >2dp stays
        // visible and intact; getLineTotal and the submit guard reject it.
        const value = rawValue;
        let qty = line.qty;
        let cost_price = line.cost_price;
        let discount = line.discount;
        let total = line.total;

        const lastEdited: Line["lastEdited"] =
          field === "cost_price" || field === "discount" || field === "total"
            ? field
            : undefined;

        if (field === "qty") {
          qty = value;
        } else if (field === "cost_price") {
          cost_price = value;
        } else if (field === "discount") {
          discount = value;
        } else if (field === "total") {
          total = value;
        }

        let totalError: string | undefined;
        let totalNotice: string | undefined;

        if (field === "total") {
          // PURCHASE-LINE-ROUND-STAGE-1 editable-total: solve a canonical 2dp
          // cost from the entered total, holding the discount FIXED (never
          // reverse-derived). Keep the total itself as raw text until blur so
          // controlled-input rerenders do not interrupt character-by-character
          // typing (for example, turning the first "1" of "100" into "1.00").
          const inv = deriveCostFromTotal(total, qty, discount);
          if (!inv.ok) {
            // Blank and a lone decimal point are viable editing states. Other
            // malformed/>2dp text stays visible and is reported immediately;
            // every invalid state is independently rejected at blur/submit.
            if (total !== "" && total !== ".") {
              totalError = "Total must be a valid amount with at most 2 decimal places.";
            }
          } else if (inv.action === "derive_cost") {
            cost_price = inv.costText;
          } else {
            // action === "preserve_cost" (100% discount): keep existing cost.
            if (!inv.exact) {
              // Nonzero total at 100% discount is not representable: keep the
              // raw entered text, error, block submission -- never silently
              // replace it with 0.00.
              totalError = "At 100% discount the total must be 0.00.";
            }
          }
        } else {
          // A non-total edit re-derives the total from cost; clear any stale
          // total-entry error/notice.
          total = getLineTotal(qty, cost_price, discount);
        }

        return markLocalLine({
          ...line,
          qty,
          cost_price,
          discount,
          total,
          lastEdited,
          totalError,
          totalNotice,
        });
      }),
    );
  };

  const handleBlur = (idx: number, field: keyof Line, value: string) => {
    // PURCHASE-LINE-ROUND-STAGE-1: no silent 2dp truncation on blur for ANY
    // field -- excess precision is rejected at submit, never quietly rounded.
    setLines((lines) =>
      lines.map((line, i) => {
        if (i !== idx) return line;
        if (field === "total") {
          const inv = deriveCostFromTotal(value, line.qty, line.discount);
          if (!inv.ok) {
            return markLocalLine({
              ...line,
              total: value,
              totalError: "Total must be a valid amount with at most 2 decimal places.",
              totalNotice: undefined,
            });
          }
          if (inv.action === "preserve_cost" && !inv.exact) {
            return markLocalLine({
              ...line,
              total: value,
              totalError: "At 100% discount the total must be 0.00.",
              totalNotice: undefined,
            });
          }
          return markLocalLine({
            ...line,
            cost_price: inv.action === "derive_cost" ? inv.costText : line.cost_price,
            total: inv.canonicalTotalText,
            totalError: undefined,
            totalNotice: inv.exact ? undefined : `Adjusted to ${inv.canonicalTotalText}`,
          });
        }
        let v = value;
        if (v.endsWith(".")) v = v.slice(0, -1);
        const next = { ...line, [field]: v };
        return localLineMessages.has(line) ? markLocalLine(next) : next;
      }),
    );
  };

  const addLine = () => setLines([...lines, { ...defaultLine }]);
  const removeLine = (idx: number) =>
    setLines((lines) => lines.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Synchronous guard against a second, near-simultaneous submit event --
    // see purchaseSubmitInFlightRef's declaration. Placed before any state
    // update so it can never be bypassed by a click that lands before
    // `saving` has re-rendered the disabled button.
    if (purchaseSubmitInFlightRef.current) {
      return;
    }

    setError("");
    setPaymentError(null);

    if (!company_id) {
      setError(localMessage("purchases.errors.missingCompany"));
      return;
    }

    // --- basic validation ---
    if (!vendorId || !vendors.find((v) => String(v.id) === vendorId)) {
      setError(localMessage("purchases.errors.selectVendor"));
      return;
    }
    if (lines.some((l) => !l.product_id)) {
      setError(localMessage("purchases.errors.selectItems"));
      return;
    }

    if (onAccount && dueDate) {
      // The due date is optional (a deliberate clear = "no due date", sent as
      // an explicit null). Only validate the floor when a date is present.
      if (dueDate < date) {
        setError(localMessage("purchases.errors.entryDueDate"));
        return;
      }
    }

    // --- precision policy (PURCHASE-LINE-ROUND-STAGE-1): reject, never
    // silently round, any qty/cost/discount with more than two decimals.
    for (const line of lines) {
      const fieldErr =
        exceeds2dp(line.qty)
          ? "Quantity"
          : exceeds2dp(line.cost_price)
            ? "Unit cost"
            : exceeds2dp((line.discount ?? "").trim() === "" ? "0" : line.discount)
              ? "Discount"
              : null;
      if (fieldErr) {
        setError({ ...localMessage("purchases.errors.precision"), field: fieldErr === "Quantity" ? localMessage("purchases.errors.quantityField") : fieldErr === "Unit cost" ? localMessage("purchases.errors.unitCostField") : localMessage("sales.common.discount") });
        return;
      }
      // When the total was the last field edited, re-confirm the raw total-entry
      // state is valid (invalid/>2dp total, or a nonzero total at 100% discount,
      // must block submission -- never silently ignored because the payload
      // total is recomputed from cost).
      if (line.lastEdited === "total") {
        const inv = deriveCostFromTotal(
          line.total,
          line.qty,
          (line.discount ?? "").trim() === "" ? "0" : line.discount,
        );
        if (!inv.ok) {
          setError(localMessage("purchases.errors.invalidTotal"));
          return;
        }
        if (inv.action === "preserve_cost" && !inv.exact) {
          setError(localMessage("purchases.errors.fullDiscount"));
          return;
        }
      }
    }

    // Finalize Total-edited lines from this render's immutable snapshot. This
    // makes keyboard/programmatic submit equivalent to blur without relying on
    // a React state update having committed first.
    const finalizedLines = lines.map((line) => {
      if (line.lastEdited !== "total") return line;
      const discStr = (line.discount ?? "").trim() === "" ? "0" : line.discount;
      const inv = deriveCostFromTotal(line.total, line.qty, discStr);
      // The validation loop above guarantees this branch is unreachable.
      if (!inv.ok || (inv.action === "preserve_cost" && !inv.exact)) return line;
      return markLocalLine({
        ...line,
        cost_price: inv.action === "derive_cost" ? inv.costText : line.cost_price,
        total: inv.canonicalTotalText,
        totalError: undefined,
        totalNotice: inv.exact ? undefined : `Adjusted to ${inv.canonicalTotalText}`,
      });
    });

    // Update what the user sees, but build the POST below directly from the
    // same local finalizedLines value--never from asynchronously updated state.
    setLines(finalizedLines);

    // --- build line items for backend ---
    // Canonical net-unit-first: discount is the validated <=2dp value (never
    // reverse-derived), total is the exact canonical amount the backend will
    // recompute and persist.
    const linesForBackend = finalizedLines.map((line) => {
      const discStr = (line.discount ?? "").trim() === "" ? "0" : line.discount;
      const calc = canonicalLineTotal(line.qty, line.cost_price, discStr);
      const totalNum = calc.ok ? Number(calc.totalText) : 0;
      return {
        product_id: Number(line.product_id),
        qty: Number(line.qty) || 0,
        cost_price: Number(line.cost_price) || 0,
        discount: Number(discStr) || 0,
        total: totalNum,
      };
    });

    // One two-decimal purchase amount is the authority for the payload,
    // payment ceiling, and an untouched/default full payment.
    const canonicalPurchaseAmount = Number(
      linesForBackend.reduce((sum, line) => sum + line.total, 0).toFixed(2),
    );
    const parsedPayment = parseMoneyInput(paymentAmount);
    const effectivePayment = onAccount
      ? 0
      : paymentTouched
        ? parsedPayment
        : canonicalPurchaseAmount;
    const hasPayment =
      !onAccount &&
      (paymentTouched ? paymentAmount.trim() !== "" : canonicalPurchaseAmount > 0) &&
      effectivePayment > 0;

    // Synchronize an untouched default for display, but never read this state
    // update for submit correctness and never overwrite user-touched intent.
    if (!onAccount && !paymentTouched) {
      setPaymentAmount(canonicalPurchaseAmount.toFixed(2));
    }

    if (hasPayment) {
      if (effectivePayment <= 0) {
        setPaymentError(localMessage("purchases.errors.positivePayment"));
        return;
      }
      if (effectivePayment > canonicalPurchaseAmount) {
        setPaymentError(
          localMessage("purchases.errors.submitAboveTotal", { total: canonicalPurchaseAmount.toFixed(2) }),
        );
        return;
      }
      if (!paymentAccountId) {
        setPaymentError(localMessage("purchases.errors.selectPaymentAccount"));
        return;
      }
    }

    // On-account: send the (prefilled/edited) date, or an explicit null when
    // deliberately cleared. Non-account: omit (backend defaults to purchase.date).
    const dueDateForPayload = onAccount ? (dueDate ? dueDate : null) : null;
    const invoiceNumberForPayload =
      invoiceNumber && invoiceNumber.trim() !== "" ? invoiceNumber.trim() : null;
    const sourceDocumentIdForPayload = appliedScannerHandoff?.sourceDocumentId ?? null;

    // Bind (first attempt) or verify (retry) this attempt's identity BEFORE
    // sending anything -- see submittedPurchaseFingerprintRef's declaration
    // for why an edited retry must never reach apiPost.
    const canonicalPayload = canonicalPurchaseCreate(
      vendorId,
      date,
      dueDateForPayload,
      invoiceNumberForPayload,
      memo,
      sourceDocumentIdForPayload,
      linesForBackend,
      hasPayment ? effectivePayment : 0,
      hasPayment ? paymentAccountId : null,
    );
    const currentFingerprint = fingerprintPurchaseCreate(canonicalPayload);
    const identityDecision = decidePurchaseSubmissionIdentity(
      submittedPurchaseFingerprintRef.current,
      currentFingerprint,
    );

    if (identityDecision === "mismatch") {
      // A prior attempt under the retained key may already have committed
      // -- sending this edited payload now would either create a second,
      // different Purchase under a fresh identity (if the first attempt
      // never reached the server) or silently discard these edits (if the
      // backend replays the ORIGINAL Purchase). Block instead of guessing.
      setError(localMessage("purchases.retry.createAmbiguous"));
      return;
    }

    const idempotencyKey = getOrCreatePurchaseIdempotencyKey(
      purchaseIdempotencyKeyRef,
      randomUuidKey,
    );
    if (identityDecision === "first-attempt") {
      submittedPurchaseFingerprintRef.current = currentFingerprint;
      persistPurchaseIdempotencyState(idempotencyKey, currentFingerprint);
    }
    // identityDecision === "unchanged-retry": same key, same fingerprint,
    // nothing to (re)persist -- proceed exactly as a first attempt would.

    purchaseSubmitInFlightRef.current = true;
    setSaving(true);

    // --- payload matches PurchaseCreate ---
    const payload: any = {
      date,
      vendor_id: Number(vendorId),
      lines: linesForBackend,
      amount: canonicalPurchaseAmount,
      memo,
      idempotency_key: idempotencyKey,
    };

    if (sourceDocumentIdForPayload) {
      payload.source_document_id = sourceDocumentIdForPayload;
    }

    if (onAccount) {
      // Prefilled/edited date, or explicit null on deliberate clear.
      payload.due_date = dueDate ? dueDate : null;
    }

    if (invoiceNumberForPayload) {
      payload.invoice_number = invoiceNumberForPayload;
    }

    // 🔵 INLINE PAYMENT: this is what triggers VendorPayment + payment journal
    if (hasPayment) {
      payload.amount_paid = effectivePayment;
      payload.payment_account_id = paymentAccountId;
    }
    // If onAccount or no valid payment, we just create an unpaid bill.

    try {
      // include company_id like SaleEntry does (safe even if backend ignores)
      await apiPost<CreatedPurchase>(
        `/purchases?company_id=${company_id}`,
        payload,
      );
      if (onAccount) {
        writeLastPaymentMethod("purchases", company_id, "on_account");
      } else if (paymentAccountId) {
        writeLastPaymentMethod("purchases", company_id, paymentAccountId);
      }

      // Confirmed success -- resetPurchaseEntry() clears both idempotency
      // refs back to idle (see its own comment).
      stopDraftAutosave();
      clearDraft(PURCHASE_DRAFT_KEY);
      resetPurchaseEntry();
      navigate(companyRoute(company_id!, "/purchases"));
    } catch (err: any) {
      const failureClass = classifyPurchaseCreateFailure(err, isClosedPeriodError);

      if (failureClass !== "ambiguous") {
        // Keep the existing reset policy for definite/closed-period/conflict
        // responses. A conflict can refer to an already-persisted DIFFERENT
        // request; clearing this client's identity does not release that key
        // on the server. Clear the persisted pair as well as the refs.
        purchaseIdempotencyKeyRef.current = null;
        submittedPurchaseFingerprintRef.current = null;
        persistPurchaseIdempotencyState(null, null);
      }
      // ambiguous: deliberately retain both refs (already set above,
      // before the network call) so an unchanged retry safely replays.

      if (failureClass === "closed-period") {
        setError(localClosedPeriod(closedThroughDate));
        return;
      }

      if (failureClass === "idempotency-conflict") {
        setError(localMessage("purchases.retry.createConflict"));
        return;
      }

      const detail = localApiDetail(err);

      if (detail) {
        setError(detail);
      } else {
        setError(localMessage("purchases.errors.create"));
      }
    } finally {
      setSaving(false);
      purchaseSubmitInFlightRef.current = false;
    }
  };

  const cellInput = `${A.inputBase} px-2 text-right font-medium ${A.num} shadow-none appearance-none`;

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center justify-start px-2 py-8 ${A.body}`}
    >
      {/* Page Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => {
            clearDraft(PURCHASE_DRAFT_KEY);
            resetPurchaseEntry();
            navigate(-1);
          }}
          className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} text-center flex-1`}>{t("purchases.common.new")}</h2>

        <button
          type="button"
          onClick={() => {
            clearDraft(PURCHASE_DRAFT_KEY);
            resetPurchaseEntry();
            navigate(companyRoute(company_id!, "/"));
          }}
          className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Form Card */}
      <form
        onSubmit={handleSubmit}
        className={`${A.card} max-w-3xl w-full p-6 sm:p-8`}
      >
        {appliedScannerHandoff && (
          <div className="mb-4 rounded-2xl border border-appleblue/20 bg-appleblue/5 px-4 py-3 text-[13px] text-gray-600">
            <span className="font-medium text-gray-700">{t("purchaseScanner.scannedVendor")}</span>{" "}
            {appliedScannerHandoff.vendorText}{t("purchaseScanner.allocationHelp")}
          </div>
        )}

        {/* Vendor + Supplier Invoice No. */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
          {/* Vendor (col-span-2) */}
          <div className="w-full lg:col-span-2">
            <label className={`block ${A.label} mb-1`}>{t("directory.vendors.singular")}</label>

            <div className="flex flex-col w-full gap-2">
              {/* Vendor select + blue + */}
              <div className="flex items-center w-full gap-2 min-w-0">
                <div className="flex-1 min-w-0">
                  <AppleComboBox
                    emptyText={t("sales.shared.noResults")}
                    toggleOptionsLabel={t("sales.shared.toggleOptions")}
                    className="w-full"
                    options={vendors}
                    value={
                      vendors.find((v) => String(v.id) === vendorId) || null
                    }
                    onChange={(v) => setVendorId(v ? String(v.id) : "")}
                    getOptionLabel={(opt) => (opt ? opt.name : "")}
                    placeholder={t("purchases.entry.selectVendor")}
                  />
                </div>

                <button
                  type="button"
                  title={t("directory.vendors.new")}
                  aria-label={t("directory.vendors.new")}
                  className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 transition shrink-0 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
                  onClick={() => setShowVendorInlay(true)}
                  tabIndex={-1}
                >
                  <PlusIcon className="w-4 h-4 text-appleblue" />
                </button>
              </div>

              {vendorEmail && (
                <input
                  type="email"
                  className={`${A.inputBase} bg-gray-50 text-gray-400 cursor-not-allowed`}
                  value={vendorEmail}
                  readOnly
                  placeholder={t("directory.common.email")}
                  tabIndex={-1}
                />
              )}
            </div>
          </div>

          {/* Invoice Date + Supplier Invoice No. (col-span-2) */}
          <div className="w-full lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Invoice Date (this is your Purchase date field) */}
              <div className="w-full">
                <label className={`block ${A.label} mb-1`}>
                  {t("purchases.common.invoiceDate")}
                </label>
                <AppleDateInput
                  openCalendarLabel={t("sales.shared.openCalendar")}
                  valueYmd={date}
                  onChangeYmd={(ymd) => setDate(ymd)}
                  ariaLabel={t("purchases.common.invoiceDate")}
                />
                {isClosedDate && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                    {t("sales.errors.closedWarning")}
                  </div>
                )}
              </div>

              {/* Supplier Invoice No. */}
              <div className="w-full">
                <label className={`block ${A.label} mb-1`}>
                  {t("purchases.common.supplierInvoice")}
                </label>
                <input
                  type="text"
                  className={A.inputBase}
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder={t("purchases.entry.invoicePlaceholder")}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className={`${A.card} mb-4 p-4`}>
          {/* DESKTOP */}
          <div className="hidden md:block">
            <table className="w-full table-fixed">
              <colgroup>
                <col /> {/* Item grows */}
                <col style={{ width: "4.25rem" }} /> {/* Qty */}
                <col style={{ width: "5.75rem" }} /> {/* Price */}
                <col style={{ width: "4.25rem" }} /> {/* Dis % */}
                <col style={{ width: "7.75rem" }} /> {/* Total */}
                <col style={{ width: "5.25rem" }} /> {/* Actions */}
              </colgroup>

              <thead>
                <tr className="border-b border-gray-100">
                  <th
                    className={`text-left pb-2 pl-4 pr-2 ${A.label}`}
                  >
                    {t("purchases.entry.item")}
                  </th>
                  <th
                    className={`text-right pb-2 pl-1 pr-2 ${A.label}`}
                  >
                    {t("sales.common.qty")}
                  </th>
                  <th
                    className={`text-right pb-2 pl-1 pr-2 ${A.label}`}
                  >
                    {t("purchases.entry.price")}
                  </th>
                  <th
                    className={`text-right pb-2 pl-1 pr-2 ${A.label}`}
                  >
                    {t("sales.common.discountPercent")}
                  </th>
                  <th
                    className={`text-right pb-2 pl-1 pr-2 ${A.label}`}
                  >
                    {t("sales.common.total")}
                  </th>
                  <th className="pb-2 pl-1 pr-1" />
                </tr>
              </thead>

              <tbody>
                {lines.map((line, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {/* Item */}
                    <td className="py-2 pl-4 pr-2 align-top overflow-visible">
                      <div className="relative z-20 min-w-0 w-full">
                        <AppleComboBox
                          emptyText={t("sales.shared.noResults")}
                          toggleOptionsLabel={t("sales.shared.toggleOptions")}
                          className="w-full"
                          label=""
                          options={products}
                          value={
                            products.find(
                              (p) => String(p.id) === line.product_id,
                            ) || null
                          }
                          onChange={(product) =>
                            handleItemChange(
                              i,
                              product ? String(product.id) : "",
                            )
                          }
                          getOptionLabel={(opt) => (opt ? opt.name : "")}
                          placeholder={t("purchases.entry.selectItem")}
                        />
                      </div>
                    </td>

                    {/* Qty */}
                    <td className="py-2 pl-1 pr-2 align-top">
                      <AppleNumberInput
                        inputMode="numeric"
                        className={cellInput}
                        value={line.qty}
                        data-testid={`line-qty-${i}`}
                        onChange={(e) =>
                          handleLineChange(i, "qty", e.target.value)
                        }
                        onBlur={(e) => handleBlur(i, "qty", e.target.value)}
                        autoComplete="off"
                      />
                    </td>

                    {/* Cost Price */}
                    <td className="py-2 pl-1 pr-2 align-top">
                      <AppleNumberInput
                        className={cellInput}
                        value={line.cost_price}
                        data-testid={`line-cost-${i}`}
                        onChange={(e) =>
                          handleLineChange(i, "cost_price", e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(i, "cost_price", e.target.value)
                        }
                        autoComplete="off"
                      />
                    </td>

                    {/* Discount */}
                    <td className="py-2 pl-1 pr-2 align-top">
                      <AppleNumberInput
                        className={cellInput}
                        value={line.discount}
                        data-testid={`line-disc-${i}`}
                        onChange={(e) =>
                          handleLineChange(i, "discount", e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(i, "discount", e.target.value)
                        }
                        autoComplete="off"
                        placeholder="0"
                      />
                    </td>

                    {/* Total */}
                    <td className="py-2 pl-1 pr-2 align-top">
                      <AppleNumberInput
                        className={cellInput}
                        value={line.total}
                        data-testid={`line-total-${i}`}
                        onChange={(e) =>
                          handleLineChange(i, "total", e.target.value)
                        }
                        onBlur={(e) => handleBlur(i, "total", e.target.value)}
                        autoComplete="off"
                      />
                      {line.totalError ? (
                        <div className="mt-1 text-xs text-red-600">
                          {messageText(lineMessage(line, line.totalError))}
                        </div>
                      ) : line.totalNotice ? (
                        <div className="mt-1 text-xs text-gray-500">
                          {messageText(lineMessage(line, line.totalNotice))}
                        </div>
                      ) : null}
                    </td>

                    {/* Actions */}
                    <td className="py-2 pl-1 pr-1 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title={t("directory.products.addNew")}
                          className={`${A.iconBtn} h-9 w-9 flex items-center justify-center hover:bg-gray-100 transition shrink-0 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
                          onClick={() => setProductInlayIndex(i)}
                          tabIndex={-1}
                        >
                          <PlusIcon className="w-4 h-4 text-appleblue" />
                        </button>

                        <button
                          type="button"
                          className={`${
                            A.iconBtn
                          } h-9 w-9 transition shrink-0 focus:outline-none focus:ring-2 focus:ring-appleblueD ${
                            lines.length === 1
                              ? "cursor-not-allowed hover:bg-transparent"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => removeLine(i)}
                          disabled={lines.length === 1}
                          title={t("sales.common.remove")}
                          aria-label={t("sales.common.remove")}
                        >
                          <TrashIcon
                            className={`w-5 h-5 ${
                              lines.length === 1
                                ? "text-gray-300"
                                : "text-appleblue"
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                className="text-[13px] font-normal text-appleblue bg-transparent border-none shadow-none px-0 py-0 hover:text-appleblueD focus:outline-none transition-colors"
                onClick={addLine}
                style={{ alignSelf: "flex-start" }}
              >
                {t("purchases.entry.addItem")}
              </button>
            </div>
          </div>

          {/* MOBILE (stacked layout) */}
          <div className="md:hidden">
            {lines.map((line, i) => (
              <div
                key={i}
                className="mb-3 rounded-2xl border border-gray-200 bg-white p-3"
              >
                {/* Item */}
                <div className="min-w-0">
                  <label className={`block ${A.label} mb-1`}>
                    {t("purchases.entry.item")}
                  </label>

                  <div className="min-w-0">
                    <AppleComboBox
                      emptyText={t("sales.shared.noResults")}
                      toggleOptionsLabel={t("sales.shared.toggleOptions")}
                      className="w-full"
                      options={products}
                      value={
                        products.find(
                          (p) => String(p.id) === line.product_id,
                        ) || null
                      }
                      onChange={(product) =>
                        handleItemChange(i, product ? String(product.id) : "")
                      }
                      getOptionLabel={(opt) => (opt ? opt.name : "")}
                      placeholder={t("purchases.entry.selectItem")}
                    />
                  </div>

                  <div className="mt-2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      title={t("directory.products.addNew")}
                      aria-label={t("directory.products.addNew")}
                      className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-100 transition shrink-0 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
                      onClick={() => setProductInlayIndex(i)}
                      tabIndex={-1}
                    >
                      <PlusIcon className="w-4 h-4 text-appleblue" />
                    </button>

                    <button
                      type="button"
                      className={`${
                        A.iconBtn
                      } flex items-center justify-center transition shrink-0 focus:outline-none focus:ring-2 focus:ring-appleblueD ${
                        lines.length === 1
                          ? "cursor-not-allowed hover:bg-transparent"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() => removeLine(i)}
                      disabled={lines.length === 1}
                      title={t("sales.common.remove")}
                      aria-label={t("sales.common.remove")}
                    >
                      <TrashIcon
                        className={`w-5 h-5 ${
                          lines.length === 1
                            ? "text-gray-300"
                            : "text-appleblue"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Qty */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.qty")}
                    </label>
                    <AppleNumberInput
                      inputMode="numeric"
                      className={`${A.inputBase} text-right font-medium ${A.num} appearance-none`}
                      value={line.qty}
                      onChange={(e) =>
                        handleLineChange(i, "qty", e.target.value)
                      }
                      onBlur={(e) => handleBlur(i, "qty", e.target.value)}
                      autoComplete="off"
                    />
                  </div>

                  {/* Cost Price */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("purchases.common.costPrice")}
                    </label>
                    <AppleNumberInput
                      className={`${A.inputBase} text-right font-medium ${A.num} appearance-none`}
                      value={line.cost_price}
                      onChange={(e) =>
                        handleLineChange(i, "cost_price", e.target.value)
                      }
                      onBlur={(e) =>
                        handleBlur(i, "cost_price", e.target.value)
                      }
                      autoComplete="off"
                    />
                  </div>

                  {/* Discount */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.discountPercent")}
                    </label>
                    <AppleNumberInput
                      className={`${A.inputBase} text-right font-medium ${A.num} appearance-none`}
                      value={line.discount}
                      onChange={(e) =>
                        handleLineChange(i, "discount", e.target.value)
                      }
                      onBlur={(e) => handleBlur(i, "discount", e.target.value)}
                      autoComplete="off"
                      placeholder="0"
                    />
                  </div>

                  {/* Total */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t("sales.common.total")}
                    </label>
                    <AppleNumberInput
                      className={`${A.inputBase} text-right font-medium ${A.num} appearance-none`}
                      value={line.total}
                      onChange={(e) =>
                        handleLineChange(i, "total", e.target.value)
                      }
                      onBlur={(e) => handleBlur(i, "total", e.target.value)}
                      autoComplete="off"
                    />
                    {line.totalError ? (
                      <div className="mt-1 text-xs text-red-600">
                        {messageText(lineMessage(line, line.totalError))}
                      </div>
                    ) : line.totalNotice ? (
                      <div className="mt-1 text-xs text-gray-500">
                        {messageText(lineMessage(line, line.totalNotice))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                className="text-[13px] font-normal text-appleblue bg-transparent border-none shadow-none px-0 py-0 hover:text-appleblueD focus:outline-none transition-colors"
                onClick={addLine}
                style={{ alignSelf: "flex-start" }}
              >
                {t("purchases.entry.addItem")}
              </button>
            </div>
          </div>
        </div>

        {/* Grand Total */}
        <div className="mb-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 shadow-sm">
          {scannedTotal === null || scannerDifferenceCents === null ? (
            <div className="text-right">
              <div className={A.label}>{t("sales.common.grandTotal")}</div>
              <div className={`${A.title} ${A.num} text-gray-900`}>
                {formatMoney(grandTotal)}
              </div>
            </div>
          ) : (
            <div className="ml-auto max-w-sm space-y-2">
              <div className="flex items-center justify-between gap-4">
                <span className={A.label}>{t("purchaseScanner.scannedTotal")}</span>
                <span className={`${A.body} ${A.num} font-medium text-gray-700`}>
                  {formatMoney(scannedTotal)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className={A.label}>{t("purchaseScanner.enteredTotal")}</span>
                <span className={`${A.title} ${A.num} text-gray-900`}>
                  {formatMoney(grandTotal)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-2">
                <span
                  className={`text-[13px] font-medium ${
                    scannerDifferenceCents === 0
                      ? "text-emerald-700"
                      : scannerDifferenceCents > 0
                        ? "text-appleblue"
                        : "text-amber-700"
                  }`}
                >
                  {scannerDifferenceCents === 0
                    ? t("purchaseScanner.matched")
                    : scannerDifferenceCents > 0
                      ? t("spendingDeposits.remaining")
                      : t("purchaseScanner.over")}
                </span>
                <span
                  className={`text-[13px] font-semibold tabular-nums ${
                    scannerDifferenceCents === 0
                      ? "text-emerald-700"
                      : scannerDifferenceCents > 0
                        ? "text-appleblue"
                        : "text-amber-700"
                  }`}
                >
                  {scannerDifferenceCents === 0
                    ? formatMoney(0)
                    : formatMoney(Math.abs(scannerDifferenceCents) / 100)}
                </span>
              </div>
              <p className="text-right text-xs text-gray-500">
                {t("purchaseScanner.referenceOnly")}
              </p>
            </div>
          )}
        </div>

        {/* Payment */}
        <div className="mb-4 pt-3 border-t border-gray-100">
          <div className={`${A.label} mb-2`}>{t("sales.common.payment")}</div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Account / Terms */}
            <div>
              <label className={`block ${A.label} mb-1`}>
                {t("purchases.entry.accountTerms")}
              </label>
              <AppleComboBox
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
                options={paymentAccountOptions}
                value={
                  onAccount
                    ? paymentAccountOptions[0]
                    : paymentAccountOptions.find(
                        (acc) => acc.id === paymentAccountId,
                      ) || null
                }
                onChange={(acc) => {
                  const baseDue = date || ymdTodayLocal();

                  if (!acc) {
                    setOnAccount(false);
                    setPaymentAccountId(null);
                    setDueDate(baseDue);
                    return;
                  }

                  if (acc.id === -1) {
                    // On account
                    if (!onAccount) setDueDate(baseDue);
                    setOnAccount(true);
                    setPaymentAccountId(null);
                    setPaymentAmount("");
                    setPaymentTouched(false);
                  } else {
                    // Pay now
                    if (onAccount) setDueDate(baseDue);
                    setOnAccount(false);
                    setPaymentAccountId(acc.id);
                    setPaymentTouched(false);
                  }
                }}
                preserveQueryOnDisplayChange
                getOptionSearchText={(acc) => acc.name}
                getOptionLabel={(acc) => acc.id === -1 ? t("purchases.entry.onAccount") : creditCardPaymentDisplayName(uniqueDisplayAccount(accounts, acc.id), t, acc.name)}
                placeholder={t("purchases.entry.selectAccount")}
              />
            </div>

            {/* Amount */}
            <div>
              <label
                className={`block ${A.label} mb-1 md:text-right`}
              >
                {t("sales.common.amount")}
              </label>
              <AppleNumberInput
                data-testid="purchase-payment-amount"
                className={`${A.inputBase} text-right font-medium ${A.num} ${
                  onAccount
                    ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    : "border-gray-200 bg-white text-gray-700"
                }`}
                value={onAccount ? "" : paymentAmount}
                onChange={(e) => {
                  setPaymentTouched(true);
                  setPaymentAmount(e.target.value);
                }}
                onBlur={() => {
                  if (onAccount || !paymentAmount.trim()) return;

                  const amt = parseMoneyInput(paymentAmount);

                  if (amt > grandTotal) {
                    setPaymentAmount(toInputMoney(grandTotal));
                    return;
                  }

                  if (amt < 0) {
                    setPaymentAmount("0");
                    return;
                  }

                  setPaymentAmount(toInputMoney(amt));
                }}
                placeholder={
                  onAccount
                    ? t("purchases.entry.fullOnAccount")
                    : grandTotal.toFixed(2)
                }
                disabled={onAccount}
                autoComplete="off"
              />
            </div>

            {/* Due Date (only when On account) */}
            {onAccount ? (
              <div>
                <label className={`block ${A.label} mb-1`}>
                  {t("sales.common.dueDateAria")}
                </label>
                <AppleDateInput
                  openCalendarLabel={t("sales.shared.openCalendar")}
                  valueYmd={dueDate}
                  onChangeYmd={onDueDateChange}
                  minYmd={date}
                  allowEmpty
                  ariaLabel={t("sales.common.dueDateAria")}
                />
                <p className="mt-1 text-[11px] text-gray-400">
                  {t("purchases.entry.dueHelp")}
                </p>
              </div>
            ) : (
              // keep 3-col spacing stable on desktop
              <div className="hidden md:block" />
            )}
          </div>

          {paymentError && (
            <div className="text-xs text-red-500 mt-1">{messageText(paymentError)}</div>
          )}
        </div>

        {/* Memo */}
        <MemoSection
          label={t("sales.common.memo")}
          placeholder={t("sales.common.memoPlaceholder")}
          mode="edit"
          value={memo}
          onChange={setMemo}
          className="mt-4 mb-8"
        />

        {/* Actions */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              clearDraft(PURCHASE_DRAFT_KEY);
              resetPurchaseEntry();
              navigate(companyRoute(company_id!, "/dashboard"), {
                replace: true,
              });
            }}
          >
            {t("directory.common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t("directory.common.savingDots") : t("sales.common.enter")}
          </Button>
        </div>

        {error && (
          <div className="text-[13px] text-red-500 font-medium mt-2">
            {messageText(error)}
          </div>
        )}
      </form>

      {/* Vendor Inlay */}
      {showVendorInlay && (
        <VendorEntry
          mode="inlay"
          title={t("directory.vendors.new")}
          variant="full"
          companyId={company_id}
          onCreated={(v) => {
            const adapted: Vendor = {
              id: Number((v as any).id),
              name: (v as any).name,
              email: (v as any).email ?? undefined,
            };
            setVendors((prev) => [...prev, adapted]);
            setVendorId(String(adapted.id));
            setVendorEmail(adapted.email ?? "");
          }}
          onClose={() => setShowVendorInlay(false)}
        />
      )}

      {/* Product Inlay */}
      {productInlayIndex !== null && (
        <ProductEntry
          mode="inlay"
          title={t("directory.products.new")}
          variant="full"
          companyId={company_id}
          onCreated={(p) => {
            const adapted: Product = {
              id: Number((p as any).id),
              name: (p as any).name,
              sell_price: Number((p as any).sell_price ?? 0),
              cost_price:
                typeof (p as any).cost_price === "number"
                  ? (p as any).cost_price
                  : undefined,
            };
            setProducts((prev) => [...prev, adapted]);

            const cp = adapted.cost_price ?? 0;
            const costStr = cp.toFixed(2);
            setLines((prev) =>
              prev.map((ln, idx) =>
                idx === productInlayIndex
                  ? {
                      ...ln,
                      product_id: String(adapted.id),
                      qty: "1",
                      cost_price: costStr,
                      discount: "0",
                      total: getLineTotal("1", costStr, "0"),
                    }
                  : ln,
              ),
            );
          }}
          onClose={() => setProductInlayIndex(null)}
        />
      )}
    </div>
  );
}
